import { IApiRepository } from 'service/protocols/api/api-repository'
import { SurgicalOrderGuidesRepository as IReportsRepository } from 'repository/interfaces/surgical-order-guides-repository'
import { CreateSurgicalOrderGuides } from 'domain/usecases/surgical-order/create-surgical-order-guides'
import { createSurgicalOrderGuidesMutation } from 'repository/graphql/mutations'
import { makeGraphQLVariable } from 'repository/graphql/utils/make-variables'
import { ApiStatusCode } from 'service/protocols/api/api-client'
import handleGraphQLError from 'repository/graphql/utils/handle-error'
import { RepositoryErrors } from 'repository/errors/repository-errors'
import { LoadSurgicalOrderGuides } from 'domain/usecases/surgical-order-guides/load-surgical-order-guides'
import { LoadSurgicalOrderGuideAuthorization } from 'domain/usecases/surgical-order-guides/load-surgical-order-guides-authorization'
import { LoadGuideItem } from 'domain/usecases/surgical-order-guides/load-guide-item'
import { CreateSurgicalOrderGuideConsultAuthorization } from 'domain/usecases/surgical-order-guides/create-surgical-order-guide-consult-authorization'

export class SurgicalOrderGuidesRepository implements IReportsRepository {
  constructor(private readonly apiRepository: IApiRepository) {}

  async createSurgicalOrderGuies(
    params: CreateSurgicalOrderGuides.Params
  ): Promise<boolean> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateSurgicalOrderGuides.Model>

    const query = createSurgicalOrderGuidesMutation

    const httpResponse = await apiRepository.post({
      url: '/graphql',
      body: {
        query: query.query,
        variables: makeGraphQLVariable(params)
      },
      query: query.name
    })

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors[httpResponse.error!])
    } else {
      return true
    }
  }

  async loadSurgicalOrderGuides(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderGuides.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderGuides.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/guides?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors.ERROR_IN_DB)
    } else {
      return {
        data: httpResponse.body
      }
    }
  }

  async loadSurgicalOrderGuidesObservations(
    surgical_order_id: number
  ): Promise<LoadSurgicalOrderGuides.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderGuides.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/guides/observations?surgical_order_id=${surgical_order_id}`
      },
      false
    )

    if (
      httpResponse.statusCode &&
      httpResponse.statusCode !== ApiStatusCode.ok
    ) {
      throw handleGraphQLError(RepositoryErrors.ERROR_IN_DB)
    } else {
      return {
        data: httpResponse.body
      }
    }
  }

  async loadSurgicalOrderGuideAuthorization(
    surgical_order_guide_id: number
  ): Promise<LoadSurgicalOrderGuideAuthorization.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadSurgicalOrderGuideAuthorization.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/guides/authorizations?surgical_order_guide_id=${surgical_order_guide_id}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async loadGuideItem(
    health_insurance_procedure_id: number,
    health_insurance_code: number
  ): Promise<LoadGuideItem.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<LoadGuideItem.Model>

    const httpResponse: any = await apiRepository.get(
      {
        url: `/surgical-order/guides/items?health_insurance_procedure_id=${health_insurance_procedure_id}&health_insurance_code=${health_insurance_code}`
      },
      false
    )

    return {
      data: httpResponse.body
    }
  }

  async createSurgicalOrderGuideConsultAuthorization(
    params: CreateSurgicalOrderGuideConsultAuthorization.Params
  ): Promise<CreateSurgicalOrderGuideConsultAuthorization.Model> {
    const apiRepository = this
      .apiRepository as IApiRepository<CreateSurgicalOrderGuideConsultAuthorization.Model>

    const httpResponse: any = await apiRepository.post({
      url: `/surgical-order/guides`,
      body: params
    })

    return httpResponse.body as unknown as CreateSurgicalOrderGuideConsultAuthorization.Model
  }
}
